import { browser } from "$app/environment"
import { isDeploymentEnv } from "$lib/environment"
import Castle from "@castleio/castle-js"
import { TwSession, type TwRestSession } from "@tastyworks/tastyworks-api"

const TW_SESSION_ID_KEY = "tw-session-id"
const TW_EMAIL_KEY = "tw-email"
const TW_NEW_USER_KEY = "tw-new-user"
const TW_EXTERNAL_ID_KEY = "tw-external-id"
const TW_USERNAME_KEY = "tw-username"
const TW_AUTH_MODE_KEY = "tw-auth-mode"

type AuthMode = "" | "bearer" | "cookie"

class Session extends TwSession implements TwRestSession {
  private readonly castle = this.initializeCastleIo()

  constructor() {
    super("AO2")
  }

  get currentEmail() {
    return sessionStorage.getItem(TW_EMAIL_KEY) as string
  }

  set currentEmail(email) {
    sessionStorage.setItem(TW_EMAIL_KEY, email)
  }

  get sessionToken() {
    return sessionStorage.getItem(TW_SESSION_ID_KEY) ?? ""
  }

  set sessionToken(token) {
    sessionStorage.setItem(TW_SESSION_ID_KEY, token)
  }

  get isNewUser() {
    return JSON.parse(sessionStorage.getItem(TW_NEW_USER_KEY) || "false")
  }

  set isNewUser(isNewUser: boolean) {
    sessionStorage.setItem(TW_NEW_USER_KEY, `${isNewUser}`)
  }

  get externalId() {
    return sessionStorage.getItem(TW_EXTERNAL_ID_KEY) as string
  }

  set externalId(externalId: string) {
    sessionStorage.setItem(TW_EXTERNAL_ID_KEY, externalId)
  }

  get username() {
    return sessionStorage.getItem(TW_USERNAME_KEY) as string
  }

  set username(username: string) {
    sessionStorage.setItem(TW_USERNAME_KEY, username)
  }

  // The transport this session was established on. Pinned on the first
  // successful validate so a live session's transport can't switch
  // mid-flight. Cleared on logout so the next session re-derives its mode.
  get authMode(): AuthMode {
    return (sessionStorage.getItem(TW_AUTH_MODE_KEY) ?? "") as AuthMode
  }

  set authMode(mode: AuthMode) {
    sessionStorage.setItem(TW_AUTH_MODE_KEY, mode)
  }

  clear() {
    sessionStorage.removeItem(TW_SESSION_ID_KEY)
    sessionStorage.removeItem(TW_EMAIL_KEY)
    sessionStorage.removeItem(TW_EXTERNAL_ID_KEY)
    sessionStorage.removeItem(TW_USERNAME_KEY)
    sessionStorage.removeItem(TW_AUTH_MODE_KEY)
  }

  override async createCastleIoRequestToken() {
    return this.castle?.createRequestToken() ?? ""
  }

  private initializeCastleIo() {
    // prevent castle from running on the server or non deployed environments
    if (!isDeploymentEnv() || !browser) return null

    return Castle.configure({
      pk: import.meta.env.VITE_CASTLE_PUBLISHABLE_API_KEY,
    })
  }
}

const session = new Session()

export default session
